import React, { useState } from 'react';
import axios, { API } from '../lib/api';
import EditableTask from './EditableTask';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Calendar, TrendingUp, ChevronDown, ChevronUp, Edit, Trash2, Archive } from 'lucide-react';
import { toast } from 'sonner';

const getDaysLeftBadgeVariant = (daysLeft) => {
  if (daysLeft > 7) return 'default';
  if (daysLeft > 0) return 'destructive';
  return 'secondary';
};

const GoalCard = ({ goal, onGoalUpdate }) => {
  const [showExtensionDialog, setShowExtensionDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showTasks, setShowTasks] = useState(false);
  const [goalTasks, setGoalTasks] = useState([]);
  const [extensionReason, setExtensionReason] = useState('');
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);

  const progress = goal.total_tasks > 0 ? (goal.completed_tasks / goal.total_tasks) * 100 : 0;
  const daysLeft = Math.ceil((new Date(goal.target_date) - new Date()) / (1000 * 60 * 60 * 24));

  const handleExtendGoal = async () => {
    try {
      await axios.post(`${API}/goals/${goal.id}/extend`, { goal_id: goal.id, reason: extensionReason });
      toast.success('Goal deadline extended');
      setShowExtensionDialog(false);
      setExtensionReason('');
      onGoalUpdate();
    } catch {
      toast.error('Failed to extend goal');
    }
  };

  const handleDeleteGoal = async () => {
    try {
      await axios.delete(`${API}/goals/${goal.id}`);
      toast.success('Goal deleted successfully');
      setShowDeleteDialog(false);
      onGoalUpdate();
    } catch {
      toast.error('Failed to delete goal');
    }
  };

  const handleArchiveGoal = async () => {
    try {
      await axios.post(`${API}/goals/${goal.id}/archive`);
      toast.success('Goal archived successfully');
      onGoalUpdate();
    } catch {
      toast.error('Failed to archive goal');
    }
  };

  const fetchGoalTasks = async () => {
    if (goalTasks.length > 0) return;
    setIsLoadingTasks(true);
    try {
      const response = await axios.get(`${API}/goals/${goal.id}/tasks`);
      setGoalTasks(response.data);
    } catch {
      toast.error('Failed to load goal tasks');
    } finally {
      setIsLoadingTasks(false);
    }
  };

  const toggleTasks = () => {
    if (!showTasks) fetchGoalTasks();
    setShowTasks(!showTasks);
  };

  const handleTaskUpdate = async (taskId, updateData) => {
    try {
      await axios.put(`${API}/tasks/${taskId}`, updateData);
      const response = await axios.get(`${API}/goals/${goal.id}/tasks`);
      setGoalTasks(response.data);
      onGoalUpdate();
      toast.success('Task updated successfully');
    } catch {
      toast.error('Failed to update task');
    }
  };

  const renderTasks = () => {
    if (isLoadingTasks) {
      return (
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-justb-primary mx-auto"></div>
          <p className="text-sm text-gray-600 mt-2">Loading tasks...</p>
        </div>
      );
    }
    if (goalTasks.length > 0) {
      return (
        <div className="space-y-3">
          {goalTasks.map((task, index) => (
            <EditableTask key={task.id} task={task} dayNumber={index + 1} onTaskUpdate={handleTaskUpdate} />
          ))}
        </div>
      );
    }
    return <p className="text-sm text-gray-500 text-center py-4">No tasks found for this goal</p>;
  };

  return (
    <Card className="hover:shadow-lg transition-all duration-300 border-l-4 border-l-justb-primary">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg font-semibold text-gray-900">{goal.title}</CardTitle>
            <p className="text-sm text-gray-600 mt-1">{goal.description}</p>
          </div>
          <div className="flex items-center space-x-2 ml-4">
            <Badge variant={getDaysLeftBadgeVariant(daysLeft)}>
              {daysLeft > 0 ? `${daysLeft} days left` : 'Overdue'}
            </Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <div className="space-y-4">
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">Progress</span>
              <span className="font-medium text-justb-primary">{goal.completed_tasks}/{goal.total_tasks} tasks</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          <div className="flex items-center justify-between text-sm text-gray-600">
            <span className="flex items-center">
              <Calendar className="mr-1 h-4 w-4" />
              Due: {new Date(goal.target_date).toLocaleDateString()}
            </span>
            <span className="flex items-center">
              <TrendingUp className="mr-1 h-4 w-4" />
              {goal.extension_count} extensions
            </span>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={toggleTasks} className="flex-1">
              {showTasks ? <ChevronUp className="mr-2 h-4 w-4" /> : <ChevronDown className="mr-2 h-4 w-4" />}
              View Tasks
            </Button>

            <Dialog open={showExtensionDialog} onOpenChange={setShowExtensionDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Edit className="mr-2 h-4 w-4" />
                  Extend
                </Button>
              </DialogTrigger>
              <DialogContent aria-describedby="extension-form-description">
                <DialogHeader>
                  <DialogTitle className="text-gray-900 font-semibold">Extend Goal Deadline</DialogTitle>
                </DialogHeader>
                <div id="extension-form-description" className="sr-only">Form to extend goal deadline</div>
                <div className="space-y-4">
                  <p className="text-sm text-gray-900">Please provide a reason for extending the deadline.</p>
                  <Textarea
                    value={extensionReason}
                    onChange={(e) => setExtensionReason(e.target.value)}
                    placeholder="e.g., Had unexpected work commitments that took priority..."
                    rows={3}
                    className="text-gray-900"
                  />
                  <div className="flex justify-end space-x-3">
                    <Button variant="outline" onClick={() => setShowExtensionDialog(false)} className="text-gray-900 border-gray-300">Cancel</Button>
                    <Button onClick={handleExtendGoal} disabled={!extensionReason.trim()} className="bg-justb-primary hover:bg-justb-primary/90">
                      Extend by 1 Day
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Button variant="outline" size="sm" onClick={handleArchiveGoal} className="text-amber-600 hover:text-amber-700 hover:bg-amber-50">
              <Archive className="h-4 w-4" />
            </Button>

            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </DialogTrigger>
              <DialogContent aria-describedby="delete-form-description">
                <DialogHeader>
                  <DialogTitle className="text-gray-900 font-semibold">Delete Goal</DialogTitle>
                </DialogHeader>
                <div id="delete-form-description" className="sr-only">Confirmation to delete goal</div>
                <div className="space-y-4">
                  <p className="text-sm text-gray-900">
                    Are you sure you want to delete "{goal.title}"? This action cannot be undone.
                  </p>
                  <div className="flex justify-end space-x-3">
                    <Button variant="outline" onClick={() => setShowDeleteDialog(false)} className="text-gray-900 border-gray-300">Cancel</Button>
                    <Button onClick={handleDeleteGoal} className="bg-red-600 hover:bg-red-700 text-white">Delete Goal</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {showTasks && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-justb-primary mb-3">Daily Tasks (Editable)</h4>
              {renderTasks()}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default GoalCard;
